import { z } from 'zod'
import { Currency } from '../../../enums/currency.enum'
import { isoDate } from '../expenses-by-category/iso-date'

export const bucketExpensesQuerySchema = z
  .object({
    from: isoDate,
    to: isoDate,
    baseCurrency: z.nativeEnum(Currency).optional(),
    /**
     * Comma-separated category ids to keep — the report page passes its current
     * selection so the table matches the stacks on screen. Omitted / empty means
     * every category, mirroring the "empty set = show all" filter model.
     */
    categoryIds: z
      .string()
      .optional()
      .transform((value) =>
        value
          ? value
              .split(',')
              .map((id) => id.trim())
              .filter(Boolean)
          : []
      ),
  })
  .refine((q) => q.from <= q.to, { message: 'from must be on or before to', path: ['from'] })

export type BucketExpensesQueryDto = z.infer<typeof bucketExpensesQuerySchema>

export const bucketExpenseItemSchema = z.object({
  id: z.string(),
  date: z.string(),
  description: z.string().nullable(),
  /** the viewer's share in the expense's own currency */
  amount: z.number(),
  currency: z.nativeEnum(Currency).or(z.string()),
  /** the viewer's share converted at the expense date's FX rate */
  convertedAmount: z.number(),
  accountId: z.string(),
  accountName: z.string(),
  categoryId: z.string(),
  categoryName: z.string(),
  categoryColor: z.string().nullable(),
})

export type BucketExpenseItemDto = z.infer<typeof bucketExpenseItemSchema>

export const bucketExpensesResponseSchema = z.object({
  baseCurrency: z.nativeEnum(Currency),
  range: z.object({
    from: z.string(),
    to: z.string(),
  }),
  items: z.array(bucketExpenseItemSchema),
  /** sum of the returned items' converted amounts — matches the clicked bar */
  total: z.number(),
  skippedExpenseCount: z.number().int().nonnegative(),
})

export type BucketExpensesResponseDto = z.infer<typeof bucketExpensesResponseSchema>
